import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Badge, Button, CopyButton, Drawer, Group, Loader, NumberInput, Paper, Stack,
  Switch, Table, TextInput, Textarea, Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconCheck, IconCopy, IconGiftCard, IconPlus, IconBan } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useGiftCards, useIssueGiftCard, useVoidGiftCard, type GiftCardDto } from './giftCardsApi';

const fmt = (n: number) => new Intl.NumberFormat('ar-PS', { minimumFractionDigits: 2, numberingSystem: 'latn' }).format(n);
const toIso = (d: Date | null) => {
  if (!d) return null;
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export function GiftCardsPage() {
  const { t, i18n } = useTranslation();
  const ar = i18n.language === 'ar';
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading } = useGiftCards(includeInactive);
  const issue = useIssueGiftCard();
  const del = useVoidGiftCard();

  const [drawer, setDrawer] = useState(false);
  const [voiding, setVoiding] = useState<GiftCardDto | null>(null);

  const rows = data ?? [];

  const form = useForm({
    initialValues: { code: '', initialBalance: 100, expiryDate: null as Date | null, notes: '' },
    validate: { initialBalance: (v) => (v > 0 ? null : t('giftCards.form.balancePositive')) },
  });

  const openCreate = () => {
    form.setValues({ code: '', initialBalance: 100, expiryDate: null, notes: '' });
    form.resetDirty();
    setDrawer(true);
  };

  const submit = form.onSubmit(async (v) => {
    try {
      const card = await issue.mutateAsync({
        code: v.code.trim() || null,
        initialBalance: v.initialBalance,
        expiryDate: toIso(v.expiryDate),
        notes: v.notes.trim() || null,
      });
      notifications.show({ color: 'green', title: t('giftCards.issued'), message: card.code });
      setDrawer(false);
    } catch (err) {
      notifications.show({
        color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody'),
      });
    }
  });

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.organization')}
        title={t('giftCards.title')}
        subtitle={t('giftCards.subtitle')}
        actions={<Button leftSection={<IconPlus size={16} stroke={1.8} />} onClick={openCreate}>{t('giftCards.issue')}</Button>}
      />

      <Group justify="flex-end">
        <Switch label={t('giftCards.showInactive')} checked={includeInactive} onChange={(e) => setIncludeInactive(e.currentTarget.checked)} />
      </Group>

      <Paper p={rows.length ? 0 : 'lg'}>
        {isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconGiftCard size={22} stroke={1.6} />} title={t('giftCards.empty')}
            description={t('giftCards.emptyBody')} actionLabel={t('giftCards.issue')} onAction={openCreate} />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('giftCards.code')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('giftCards.balance')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('giftCards.initial')}</Table.Th>
                <Table.Th>{t('giftCards.expiry')}</Table.Th>
                <Table.Th>{t('common.status')}</Table.Th>
                <Table.Th style={{ width: 80, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((g) => (
                <Table.Tr key={g.giftCardId}>
                  <Table.Td>
                    <Group gap={6} wrap="nowrap">
                      <span className="font-numeric font-mono" style={{ fontWeight: 600 }}>{g.code}</span>
                      <CopyButton value={g.code}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? t('common.copied') : t('common.copy')} withinPortal>
                            <ActionIcon variant="subtle" color="gray" size="sm" onClick={copy} aria-label={t('common.copy')}>
                              {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    </Group>
                  </Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end', fontWeight: 600 }}>₪ {fmt(g.currentBalance)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }} c="var(--wheels-ink-muted)">₪ {fmt(g.initialBalance)}</Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)" className="font-numeric">{g.expiryDate ?? '—'}</Table.Td>
                  <Table.Td>
                    {g.isActive
                      ? <Badge color="green" variant="light" radius="sm">{t('common.active')}</Badge>
                      : <Badge color="gray" variant="light" radius="sm">{t('giftCards.voided')}</Badge>}
                  </Table.Td>
                  <Table.Td>
                    <Group justify="flex-end">
                      {g.isActive && (
                        <Tooltip label={t('giftCards.void')} withinPortal>
                          <ActionIcon variant="subtle" color="red" onClick={() => setVoiding(g)} aria-label={t('giftCards.void')}>
                            <IconBan size={16} stroke={1.6} />
                          </ActionIcon>
                        </Tooltip>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer opened={drawer} onClose={() => setDrawer(false)} position={ar ? 'left' : 'right'} size="md" padding="lg" title={t('giftCards.issue')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <NumberInput label={t('giftCards.form.initialBalance')} withAsterisk min={0} step={10} decimalScale={2}
              {...form.getInputProps('initialBalance')} />
            <TextInput label={t('giftCards.form.code')} description={t('giftCards.form.codeHelp')} {...form.getInputProps('code')} />
            <DatePickerInput label={t('giftCards.form.expiry')} clearable valueFormat="YYYY-MM-DD" {...form.getInputProps('expiryDate')} />
            <Textarea label={t('giftCards.form.notes')} autosize minRows={2} {...form.getInputProps('notes')} />
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={issue.isPending}>{t('giftCards.issue')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>

      <ConfirmDialog opened={voiding !== null} onClose={() => setVoiding(null)}
        onConfirm={async () => {
          if (!voiding) return;
          try { await del.mutateAsync(voiding.giftCardId); setVoiding(null); }
          catch { notifications.show({ color: 'red', title: t('common.toasts.errorTitle'), message: t('common.toasts.errorBody') }); }
        }}
        title={t('giftCards.voidTitle')} message={t('giftCards.voidBody', { code: voiding?.code ?? '' })} loading={del.isPending} />
    </Stack>
  );
}
